(() => {
  if (customElements.get('lake-health-quiz')) return;

  class LakeHealthQuiz extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.form = this.querySelector('[data-quiz-form]');
      this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
      this.results = Array.from(this.querySelectorAll('[data-quiz-result]'));
      this.progress = this.querySelector('[data-quiz-progress]');
      this.progressLabel = this.querySelector('[data-quiz-progress-label]');
      this.liveRegion = this.querySelector('[data-quiz-live]');
      this.nextButton = this.querySelector('[data-quiz-next]');
      this.restartButtons = Array.from(this.querySelectorAll('[data-quiz-restart]'));

      if (!this.form || this.steps.length !== 2 || this.results.length === 0) return;

      this.classList.add('is-enhanced');
      this.querySelectorAll('[data-quiz-js-only]').forEach((element) => {
        element.hidden = false;
      });
      this.steps[1].hidden = true;
      this.results.forEach((result) => {
        result.hidden = true;
      });
      this.updateProgress(1);

      this.nextButton?.addEventListener('click', () => this.advance());
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.form.addEventListener('change', (event) => this.clearError(event));
      this.restartButtons.forEach((button) => {
        button.addEventListener('click', () => this.restart());
      });
      this.addEventListener('click', (event) => this.trackCta(event));

      this.emit('start', { step: 1 });
    }

    selectedValue(name) {
      return this.form.querySelector(`input[name="${name}"]:checked`)?.value || '';
    }

    advance() {
      const context = this.selectedValue('lake-health-context');
      if (!context) {
        this.showError(1, 'Choose a waterbody type to continue.');
        return;
      }

      this.emit('step_complete', { step: 1, answer: context });

      if (context === 'new') {
        this.complete('new-pond', context);
        return;
      }

      this.steps[0].hidden = true;
      this.steps[1].hidden = false;
      this.updateProgress(2);
      this.announce('Question 2 of 2. Choose the observation or goal that best matches your reason for visiting.');
      this.steps[1].querySelector('legend')?.focus();
    }

    submit(event) {
      event.preventDefault();
      const context = this.selectedValue('lake-health-context');
      const observation = this.selectedValue('lake-health-observation');

      if (!context) {
        this.steps[1].hidden = true;
        this.steps[0].hidden = false;
        this.updateProgress(1);
        this.showError(1, 'Choose a waterbody type to continue.');
        return;
      }

      if (!observation) {
        this.showError(2, 'Choose one observation or goal to view useful next links.');
        return;
      }

      this.emit('step_complete', { step: 2, answer: observation });
      this.complete(observation, context);
    }

    complete(resultCode, context) {
      const result = this.results.find((panel) => panel.dataset.quizResult === resultCode);
      if (!result) return;

      const contactFirstContexts = ['community', 'agricultural', 'golf', 'municipal', 'waterfront', 'other'];
      const reviewFirstResults = ['algae', 'clarity', 'winter', 'fish', 'unsure'];
      this.toggleAttribute(
        'data-contact-first',
        contactFirstContexts.includes(context) || reviewFirstResults.includes(resultCode)
      );
      this.updateContactLinks(context, resultCode);
      this.steps.forEach((step) => {
        step.hidden = true;
      });
      this.results.forEach((panel) => {
        panel.hidden = panel !== result;
      });
      this.updateProgress(2, true);
      this.announce('Your next places to explore are ready.');
      result.querySelector('[data-quiz-result-heading]')?.focus();
      this.emit('complete', { result: resultCode });
    }

    updateContactLinks(context, resultCode) {
      this.querySelectorAll('[data-quiz-cta="contact"]').forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('pond_profile', '1');
        url.searchParams.set('profile_context', context);
        url.searchParams.set('profile_need', resultCode);
        url.hash = 'ContactForm';
        link.href = url.toString();
      });
    }

    restart() {
      this.form.reset();
      this.removeAttribute('data-contact-first');
      this.results.forEach((result) => {
        result.hidden = true;
      });
      this.steps[1].hidden = true;
      this.steps[0].hidden = false;
      this.querySelectorAll('[data-quiz-error]').forEach((error) => {
        error.hidden = true;
      });
      this.querySelectorAll('[aria-invalid="true"]').forEach((group) => {
        group.removeAttribute('aria-invalid');
      });
      this.updateProgress(1);
      this.announce('Quiz restarted. Question 1 of 2.');
      this.steps[0].querySelector('legend')?.focus();
      this.emit('restart', { step: 1 });
    }

    showError(stepNumber, message) {
      const step = this.steps[stepNumber - 1];
      const group = step?.querySelector('[data-quiz-options]');
      const error = step?.querySelector('[data-quiz-error]');
      if (!step || !group || !error) return;

      group.setAttribute('aria-invalid', 'true');
      error.hidden = false;
      error.textContent = message;
      this.announce(message);
      step.querySelector('input')?.focus();
      this.emit('validation_error', { step: stepNumber });
    }

    clearError(event) {
      if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
      const step = event.target.closest('[data-quiz-step]');
      step?.querySelector('[data-quiz-options]')?.removeAttribute('aria-invalid');
      const error = step?.querySelector('[data-quiz-error]');
      if (error) error.hidden = true;
    }

    updateProgress(step, complete = false) {
      if (!this.progress || !this.progressLabel) return;
      const value = complete ? 2 : step;
      this.progress.setAttribute('aria-valuenow', String(value));
      this.progress.dataset.currentStep = String(value);
      this.progress.classList.toggle('is-complete', complete);
      this.progressLabel.textContent = complete ? 'Complete' : `Question ${step} of 2`;
    }

    trackCta(event) {
      const link = event.target.closest('[data-quiz-cta]');
      if (!link || !this.contains(link)) return;
      const result = link.closest('[data-quiz-result]')?.dataset.quizResult || 'noscript';
      this.emit('cta_click', { result, cta: link.dataset.quizCta || 'unknown' });
    }

    announce(message) {
      if (this.liveRegion) this.liveRegion.textContent = message;
    }

    emit(action, details = {}) {
      this.dispatchEvent(
        new CustomEvent('laketech:quiz', {
          bubbles: true,
          detail: {
            quiz: 'pond-profile',
            version: 2,
            action,
            ...details,
          },
        })
      );
    }
  }

  customElements.define('lake-health-quiz', LakeHealthQuiz);
})();
